import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  ChevronRight,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { useAnimationStore } from '../../store/animationStore';
import {
  PropertyTrack as PropertyTrackType,
  PROPERTY_LABELS,
} from '../../../core/types/animation.types';
import { KeyframeDiamond } from './KeyframeDiamond';

interface PropertyTrackProps {
  track: PropertyTrackType;
  animationId: string;
  trackHeaderWidth: number;
  timelineWidth: number;
  pixelsPerSecond: number;
  scrollLeft: number;
  currentTime: number;
  duration: number;
}

export const PropertyTrack: React.FC<PropertyTrackProps> = ({
  track,
  animationId,
  trackHeaderWidth,
  timelineWidth,
  pixelsPerSecond,
  scrollLeft,
  currentTime,
  duration,
}) => {
  const {
    selectedTrackIds,
    selectTrack,
    updateTrack,
    removeTrack,
    toggleTrackVisibility,
    toggleTrackLock,
    addKeyframe,
    getValueAtTime,
  } = useAnimationStore();

  const [isHovered, setIsHovered] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);

  const isSelected = selectedTrackIds.includes(track.id);
  const propertyLabel = PROPERTY_LABELS[track.property] || track.property;

  // Calculate playhead position
  const playheadX = currentTime * pixelsPerSecond - scrollLeft;

  // Handle track header click to select
  const handleHeaderClick = useCallback(
    (e: React.MouseEvent) => {
      const addToSelection = e.shiftKey || e.ctrlKey || e.metaKey;
      selectTrack(track.id, addToSelection);
    },
    [track.id, selectTrack]
  );

  // Toggle expanded state
  const handleToggleExpanded = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      updateTrack(animationId, track.id, { expanded: !track.expanded });
    },
    [animationId, track.id, track.expanded, updateTrack]
  );

  // Handle visibility toggle
  const handleToggleVisibility = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleTrackVisibility(animationId, track.id);
    },
    [animationId, track.id, toggleTrackVisibility]
  );

  // Handle lock toggle
  const handleToggleLock = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleTrackLock(animationId, track.id);
    },
    [animationId, track.id, toggleTrackLock]
  );

  // Handle delete track
  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      removeTrack(animationId, track.id);
    },
    [animationId, track.id, removeTrack]
  );

  // Handle timeline click to add keyframe
  const handleTimelineClick = useCallback(
    (e: React.MouseEvent) => {
      if (track.locked) return;

      const rect = timelineRef.current?.getBoundingClientRect();
      if (!rect) return;

      const clickX = e.clientX - rect.left;
      const time = (clickX + scrollLeft) / pixelsPerSecond;
      const clampedTime = Math.max(0, Math.min(time, duration));

      // Get current value at this time for the new keyframe
      const currentValue = getValueAtTime(animationId, track.id, clampedTime);
      const defaultValue = getDefaultValueForProperty(track.property, currentValue);

      addKeyframe(animationId, track.id, clampedTime, defaultValue);
    },
    [track.locked, track.id, track.property, animationId, scrollLeft, pixelsPerSecond, duration, addKeyframe, getValueAtTime]
  );

  // Render interpolation segments between keyframes
  const interpolationSegments = useMemo(() => {
    if (track.keyframes.length < 2) return null;

    const segments: React.ReactNode[] = [];
    for (let i = 0; i < track.keyframes.length - 1; i++) {
      const kf1 = track.keyframes[i];
      const kf2 = track.keyframes[i + 1];

      const x1 = kf1.time * pixelsPerSecond - scrollLeft;
      const x2 = kf2.time * pixelsPerSecond - scrollLeft;
      const width = x2 - x1;

      // Only render if visible in viewport
      if (x2 < 0 || x1 > timelineWidth) continue;

      segments.push(
        <div
          key={`segment-${kf1.id}-${kf2.id}`}
          className="absolute top-1/2 h-0.5 -translate-y-1/2 opacity-50"
          style={{
            left: Math.max(0, x1 + 6), // Start after the first keyframe diamond
            width: Math.min(width - 12, timelineWidth - x1), // End before the second keyframe diamond
            backgroundColor: track.color,
          }}
        />
      );
    }
    return segments;
  }, [track.keyframes, track.color, pixelsPerSecond, scrollLeft, timelineWidth]);

  return (
    <div
      className={cn(
        'flex border-b border-zinc-800 transition-colors',
        isSelected && 'bg-zinc-800/50',
        isHovered && !isSelected && 'bg-zinc-850/30'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Track Header */}
      <div
        className={cn(
          'flex items-center gap-1 px-2 py-1 border-r border-zinc-700 cursor-pointer select-none',
          'hover:bg-zinc-800/50 transition-colors'
        )}
        style={{ width: trackHeaderWidth, minWidth: trackHeaderWidth }}
        onClick={handleHeaderClick}
      >
        {/* Expand/Collapse button */}
        <button
          className="p-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
          onClick={handleToggleExpanded}
        >
          {track.expanded ? (
            <ChevronDown size={12} />
          ) : (
            <ChevronRight size={12} />
          )}
        </button>

        {/* Color indicator */}
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: track.color }}
        />

        {/* Property name */}
        <span className="text-xs text-zinc-300 truncate flex-1">
          {propertyLabel}
        </span>

        {/* Track controls */}
        <div className="flex items-center gap-0.5">
          {/* Visibility toggle */}
          <button
            className={cn(
              'p-0.5 rounded transition-colors',
              track.visible
                ? 'text-zinc-400 hover:text-zinc-200'
                : 'text-zinc-600 hover:text-zinc-400'
            )}
            onClick={handleToggleVisibility}
            title={track.visible ? 'Hide track' : 'Show track'}
          >
            {track.visible ? <Eye size={12} /> : <EyeOff size={12} />}
          </button>

          {/* Lock toggle */}
          <button
            className={cn(
              'p-0.5 rounded transition-colors',
              track.locked
                ? 'text-amber-500 hover:text-amber-400'
                : 'text-zinc-500 hover:text-zinc-300'
            )}
            onClick={handleToggleLock}
            title={track.locked ? 'Unlock track' : 'Lock track'}
          >
            {track.locked ? <Lock size={12} /> : <Unlock size={12} />}
          </button>

          {/* Delete button */}
          <button
            className="p-0.5 text-zinc-500 hover:text-red-400 rounded transition-colors"
            onClick={handleDelete}
            title="Delete track"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Timeline Area */}
      <div
        ref={timelineRef}
        className={cn(
          'relative flex-1 h-8 cursor-crosshair overflow-hidden',
          track.locked && 'cursor-not-allowed'
        )}
        onClick={handleTimelineClick}
      >
        {/* Grid lines (subtle) */}
        <div className="absolute inset-0 pointer-events-none">
          {renderGridLines(pixelsPerSecond, scrollLeft, timelineWidth)}
        </div>

        {/* Interpolation segments */}
        {interpolationSegments}

        {/* Keyframes */}
        {track.keyframes.map((keyframe) => {
          const xPos = keyframe.time * pixelsPerSecond - scrollLeft;
          // Only render if visible in viewport (with some padding)
          if (xPos < -20 || xPos > timelineWidth + 20) return null;

          return (
            <KeyframeDiamond
              key={keyframe.id}
              keyframe={keyframe}
              track={track}
              animationId={animationId}
              pixelsPerSecond={pixelsPerSecond}
              scrollLeft={scrollLeft}
              trackColor={track.color}
            />
          );
        })}

        {/* Playhead line */}
        {playheadX >= 0 && playheadX <= timelineWidth && (
          <div
            className="absolute top-0 bottom-0 w-px bg-red-500 pointer-events-none z-10"
            style={{ left: playheadX }}
          />
        )}
      </div>
    </div>
  );
};

// Helper function to get default value for a property
function getDefaultValueForProperty(
  property: string,
  currentValue: number | string | undefined
): number | string {
  if (currentValue !== undefined) {
    return currentValue;
  }

  switch (property) {
    case 'position.x':
    case 'position.y':
      return 0;
    case 'rotation':
      return 0;
    case 'scale.x':
    case 'scale.y':
      return 1;
    case 'alpha':
      return 1;
    case 'tint':
      return 0xffffff;
    case 'sprite-frame':
      return '';
    default:
      return 0;
  }
}

// Helper function to render subtle grid lines
function renderGridLines(
  pixelsPerSecond: number,
  scrollLeft: number,
  timelineWidth: number
): React.ReactNode {
  const lines: React.ReactNode[] = [];

  // Determine grid interval based on zoom level
  let gridInterval = 1; // seconds
  if (pixelsPerSecond < 50) {
    gridInterval = 2;
  } else if (pixelsPerSecond < 30) {
    gridInterval = 5;
  } else if (pixelsPerSecond > 150) {
    gridInterval = 0.5;
  } else if (pixelsPerSecond > 300) {
    gridInterval = 0.25;
  }

  const startTime = Math.floor(scrollLeft / pixelsPerSecond / gridInterval) * gridInterval;
  const endTime = (scrollLeft + timelineWidth) / pixelsPerSecond;

  for (let time = startTime; time <= endTime; time += gridInterval) {
    const x = time * pixelsPerSecond - scrollLeft;
    if (x < 0 || x > timelineWidth) continue;

    const isWholeSecond = Math.abs(time - Math.round(time)) < 0.001;

    lines.push(
      <div
        key={`grid-${time}`}
        className={cn(
          'absolute top-0 bottom-0 w-px',
          isWholeSecond ? 'bg-zinc-700' : 'bg-zinc-800'
        )}
        style={{ left: x }}
      />
    );
  }

  return lines;
}

export default PropertyTrack;
